import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo, useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { FormControlLabel, Radio } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { FilterSearchInput } from 'src/components/BeaconV2Components/ProductGrowthModal/ModalComponents/FilterSearchInput';
import { useAppSelector } from 'src/utils/Hooks';
import StyledCheckbox from 'src/components/BeaconV2Components/common/StyledCheckbox';

const MAX_OPTIONS_SHOWN = 6;

type AtlasBUOption = {
  id: string;
  displayName: string;
  isSelected: boolean;
};

interface AtlasBusinessUnitFilterGroupProps {
  filterTitle: string;
  options: AtlasBUOption[];
  onSelect: (e: any, parentTopLevelId: string) => void;
  isRadio?: boolean;
  placeholderText?: string;
  hideSearchInput?: boolean;
  selectedBusinessUnitFieldOption: Set<string>;
  parentTopLevelId: string;
}

const AtlasBusinessUnitFilterGroup = ({
  filterTitle,
  options,
  onSelect,
  isRadio = false,
  placeholderText = '',
  hideSearchInput = false,
  selectedBusinessUnitFieldOption,
  parentTopLevelId
}: AtlasBusinessUnitFilterGroupProps) => {
  const [optionsPage, setOptionsPage] = useState(1);
  const [input, setInput] = useState('');
  const theme = useStacklineTheme();
  const segments = useAppSelector((state) => state.segments);
  const { savedSearchesById } = segments;

  const userInput = useMemo(() => input, [input]);

  const availableOptions = useMemo(() => {
    if (userInput.length > 0) {
      return options
        .filter((option) => option.id !== '0' && option.displayName.toLowerCase().includes(userInput.toLowerCase()))
        .map((option) => ({ id: option.id, label: option.displayName, isSelected: option.isSelected }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return options
      .slice(0, optionsPage * MAX_OPTIONS_SHOWN)
      .filter((opt) => opt.id && opt.id !== '0')
      .map((option) => ({ id: option.id, label: option.displayName, isSelected: option.isSelected }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [optionsPage, userInput, options]);

  const showMoreOptions = () => setOptionsPage(optionsPage + 1);

  const showLessOptions = () => setOptionsPage(1);

  // Check if we've shown all options
  const totalOptions = options.filter((opt: any) => opt.id && opt.id !== '0').length;
  const hasMoreOptions = totalOptions > optionsPage * MAX_OPTIONS_SHOWN;
  const isExpanded = optionsPage > 1;
  const shouldShowButton = totalOptions > MAX_OPTIONS_SHOWN;

  return (
    <>
      <Flex flexDirection="column">
        <Flex marginTop={theme.spacing.mdl}>
          <Text variant="subtitle2">{filterTitle}</Text>
        </Flex>

        {!hideSearchInput && (
          <Flex marginTop={theme.spacing.md} marginBottom="10px">
            <FilterSearchInput handleChange={setInput} input={input} placeholderText={placeholderText} />
          </Flex>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, auto)',
            color: theme.colors.primary,
            justifyContent: 'space-between',
            rowGap: '16px',
            marginTop: '10px',
            marginBottom: theme.spacing.sm
          }}
        >
          <>
            {availableOptions ? (
              <>
                {availableOptions.map((option) => (
                  <FormControlLabel
                    title={option.label}
                    sx={{
                      gap: '8px',
                      color: theme.colors.primary,
                      minWidth: '190px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginLeft: '0px',
                      marginRight: '0px',
                      '& .MuiTypography-root': {
                        width: '160px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }
                    }}
                    checked={option.isSelected}
                    label={option.label}
                    value={option.id}
                    control={
                      isRadio ? (
                        <Radio
                          disableRipple
                          disableTouchRipple
                          sx={{
                            padding: '0',
                            svg: { height: '15px', width: '15px' },
                            '&, &.Mui-checked': { color: theme.colors.primary }
                          }}
                        />
                      ) : (
                        <StyledCheckbox iconProps={{ width: '14px', height: '14px' }} />
                      )
                    }
                    key={option.id}
                    onChange={(e) => onSelect(e, parentTopLevelId)}
                  />
                ))}
                {availableOptions.length < 3 &&
                  Array.from({ length: 3 - availableOptions.length }).map((_, index) => (
                    <div key={`placeholder-${index}`} style={{ width: '192px', display: 'hidden' }}></div>
                  ))}
              </>
            ) : null}
          </>
        </div>

        {!hideSearchInput && shouldShowButton ? (
          <button
            style={{
              paddingLeft: 0,
              border: 'none',
              background: 'none',
              textAlign: 'left',
              marginTop: theme.spacing.mdl,
              marginBottom: theme.spacing.sm
            }}
            onClick={isExpanded && !hasMoreOptions ? showLessOptions : showMoreOptions}
          >
            <Text variant="subtitle2" underlined>
              {isExpanded && !hasMoreOptions ? 'Show less' : 'Show more'}
            </Text>
          </button>
        ) : null}
      </Flex>

      {availableOptions &&
        availableOptions.length > 0 &&
        availableOptions
          .filter((availableOption) => {
            const savedSearchAvailableOption = savedSearchesById.get(availableOption.id as any) as any;
            return availableOption.isSelected && savedSearchAvailableOption.type === 'businessunit';
          })
          .map((filteredOption) => {
            const filteredOptionObject = savedSearchesById.get(filteredOption.id as any) as any;
            const nestedChildOptions = (filteredOptionObject.children as string[] | undefined)?.map((child: string) => {
              const nestedChildOption = savedSearchesById.get(child) as any;
              return { ...nestedChildOption, isSelected: selectedBusinessUnitFieldOption.has(child) };
            });
            return (
              <AtlasBusinessUnitFilterGroup
                key={filteredOption.id}
                filterTitle={filteredOption.label}
                options={nestedChildOptions as any}
                isRadio={false}
                onSelect={onSelect}
                placeholderText="Search business units"
                selectedBusinessUnitFieldOption={selectedBusinessUnitFieldOption}
                parentTopLevelId={parentTopLevelId}
              />
            );
          })}
    </>
  );
};

export default AtlasBusinessUnitFilterGroup;
